import React, { useEffect } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { StorefrontUpsell } from '@xshop/shared';
import { getProductUpsell } from '../../api/client';
import { isThemePreview } from '../../api/previewVisit';
import { useCommerce, type Commerce } from '../../commerce/ShopCommerce';
import { cartMoney } from '../../commerce/cartMoney';
import { useStoreRead } from './useStoreRead';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, textProp, type ElementProps } from './elementProps';
import { sampleOffer } from './offerSample';
import './elementOffer.css';
import { useShopWords, type ShopWords } from '../../commerce/shopWords';
import { responsive } from './responsiveImage';
import { useShopNames } from '../../commerce/shopNames';

// The upsell and downsell pages: one offer the server priced for what is in the cart, drawn the way a
// post-purchase offer page draws it — the picture beside the saving, the name, the price with the old one
// struck, then "Yes, add it" and "No thanks". Accepting adds it to the same cash-on-delivery order. A
// declined first offer can reveal a cheaper alternative on the downsell page. The builder and a theme
// preview show a sample offer made from the theme's own sample products.

/** Why there is no offer to draw, or that there is one. */
type OfferStatus = 'loading' | 'ready' | 'none' | 'empty' | 'failed';
type OfferRead = { offer: StorefrontUpsell | null; status: OfferStatus; sample: boolean };

// Reads the server's offer for the first product in the cart; the alternative is a cheaper second one.
function useLiveOffer(productId: string | null, alternative: boolean) {
  return useStoreRead(productId ? `offer:${productId}:${alternative}` : null, async () => {
    const first = await getProductUpsell(productId!);
    if (!alternative || !first) return alternative ? null : first;
    const next = await getProductUpsell(productId!, first.id);
    return next && next.unitPrice <= first.unitPrice ? next : null;
  });
}

// Works out what the element shows: a sample while the page is designed, else this order's own offer.
function useOffer(props: ElementProps, alternative: boolean, commerce: Commerce | null): OfferRead {
  const { editing } = useElementRender();
  const designing = editing || isThemePreview();
  const read = useLiveOffer(designing ? null : commerce?.product?.id ?? null, alternative);
  if (designing) return { offer: sampleOffer(props, alternative), status: 'ready', sample: true };
  if (!commerce?.lines.length) return { offer: null, status: 'empty', sample: false };
  if (read.status === 'failed' || read.status === 'missing') return { offer: null, status: 'failed', sample: false };
  if (read.status !== 'ready') return { offer: null, status: 'loading', sample: false };
  return { offer: read.data ?? null, status: read.data ? 'ready' : 'none', sample: false };
}

/** The words the merchant can change on the offer's buttons and badge. */
type OfferWords = { badge: string; accept: string; decline: string };

// Draws one offer: its picture, the saving, the name and price, and the two answers to it.
const OfferCard: React.FC<{ offer: StorefrontUpsell; words: OfferWords; money: (amount: number) => string; onAnswer: (accepted: boolean) => void }> = ({ offer, words, money, onAnswer }) => (
  <div className="xe-offer-card">
    <div className="xe-offer-media">{offer.imageUrl ? <img {...responsive(offer.imageUrl, '(max-width: 767px) 100vw, 560px')} alt={offer.productTitle} width={560} height={560} loading="lazy" /> : null}</div>
    <div className="xe-offer-body">
      <span className="xe-offer-badge">{words.badge}{offer.savings > 0 ? ` · Save ${money(offer.savings)}` : ''}</span>
      <h1 className="xe-offer-title">{offer.title}</h1>
      {offer.description ? <p className="xe-offer-text">{offer.description}</p> : null}
      <p className="xe-offer-product">{[offer.productTitle, offer.variantTitle].filter(Boolean).join(' · ')}</p>
      <p className="xe-offer-price"><strong>{money(offer.unitPrice)}</strong>{offer.compareAtPrice !== null ? <s>{money(offer.compareAtPrice)}</s> : null}</p>
      <button type="button" className="xe-offer-accept" onClick={() => onAnswer(true)}>{words.accept}</button>
      <button type="button" className="xe-offer-decline" onClick={() => onAnswer(false)}>{words.decline}</button>
      <p className="xe-offer-note"><ShieldCheck size={16} aria-hidden="true" />It ships in the same parcel, and you pay for it on delivery.</p>
    </div>
  </div>
);

/** Why the element has no offer to draw. */
type NoOfferStatus = Exclude<OfferStatus, 'ready' | 'loading'>;

// Says why there is no offer, in the store's language.
const noOfferWords = (status: NoOfferStatus, words: ShopWords): string =>
  ({ none: words.noOfferNone, failed: words.noOfferFailed, empty: words.noOfferEmpty })[status];

// Says why there is no offer, and gives the way on: to checkout with an order, back to the shop without one.
const NoOffer: React.FC<{ status: NoOfferStatus; onContinue: () => void }> = ({ status, onContinue }) => {
  const words = useShopWords();
  return (
    <div className="xe-offer-empty" role={status === 'failed' ? 'alert' : undefined}>
      <p>{noOfferWords(status, words)}</p>
      {status === 'empty'
        ? <a className="xe-offer-accept" href="/collections/all">{words.continueShopping}</a>
        : <button type="button" className="xe-offer-accept" onClick={onContinue}>{words.continueToCheckout}</button>}
    </div>
  );
};

// Stands in for the offer while it is read, shaped like it so nothing jumps when it lands.
const OfferSkeleton: React.FC = () => {
  const names = useShopNames();
  return (
    <div className="xe-offer-card" aria-busy="true" aria-label={names.loadingOffer}>
      <div className="xe-offer-media" />
      <div className="xe-offer-body"><span className="xe-offer-skel" /><span className="xe-offer-skel xe-offer-skel--wide" /><span className="xe-offer-skel" /></div>
    </div>
  );
};

// Builds what answering the offer does: add it or not, then on to checkout, or to the downsell after a first no.
function answerer(commerce: Commerce | null, read: OfferRead, alternative: boolean): (accepted: boolean) => void {
  return (accepted) => {
    // A sample's buttons go nowhere: there is no order behind it, and in the builder a click selects.
    if (read.sample || !commerce) return;
    commerce.acceptOffer(accepted ? read.offer : null);
    commerce.navigate(!accepted && read.offer && !alternative ? 'downsell' : 'checkout');
  };
}

// Reads the merchant's words for the offer's badge and buttons, falling back to the theme's own.
const offerWords = (props: ElementProps, alternative: boolean): OfferWords => ({
  badge: textProp(props, 'badge', alternative ? 'One last offer' : 'Special offer'),
  accept: textProp(props, 'acceptLabel', 'Yes, add it to my order'),
  decline: textProp(props, 'declineLabel', 'No thanks')
});

type OfferBodyProps = { read: OfferRead; words: OfferWords; money: (amount: number) => string; onAnswer: (accepted: boolean) => void };

// Draws the offer, its skeleton while it is read, or why there is none.
const OfferBody: React.FC<OfferBodyProps> = ({ read, words, money, onAnswer }) => {
  // An order with no offer is already on its way to checkout; the skeleton holds the place until it lands.
  if (read.status === 'loading' || read.status === 'none') return <OfferSkeleton />;
  if (read.status === 'ready' && read.offer) return <OfferCard offer={read.offer} words={words} money={money} onAnswer={onAnswer} />;
  return <NoOffer status={read.status === 'ready' ? 'none' : read.status} onContinue={() => onAnswer(false)} />;
};

// Draws the upsell or downsell offer for the cart, and sends the buyer on to checkout or the next offer.
export const OfferElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const commerce = useCommerce();
  const alternative = boolProp(node.props, 'alternative');
  const read = useOffer(node.props, alternative, commerce);
  // Spells an amount the way the order form will.
  const money = (amount: number) => cartMoney(amount, commerce?.currency ?? 'MAD', commerce?.direction ?? 'ltr');
  // An order with nothing to offer skips this page for checkout, in its place, so Back never returns to it.
  // Only the status is watched: the commerce value is rebuilt on every render and would navigate again each time.
  useEffect(() => { if (read.status === 'none') commerce?.navigate('checkout', true); }, [read.status]);
  return (
    <div {...box} className={[box.className, 'xe-offer'].filter(Boolean).join(' ')}>
      <OfferBody read={read} words={offerWords(node.props, alternative)} money={money} onAnswer={answerer(commerce, read, alternative)} />
    </div>
  );
};
